import React, { useEffect } from 'react';

const NCROptionsForm = ({
  ncrType,
  setNcrType,
  setQuantity,
  setsPerPad,
  setSetsPerPad,
  sheetsPerSet,
  setSheetsPerSet,
  paperSelections,
  setPaperSelections,
  sheetPrinters,
  setSheetPrinters,
  sheetSides,
  setSheetSides,
  hasWraparoundShield,
  setHasWraparoundShield,
  padGlueEdge,
  setPadGlueEdge,
  finishingOptions,
  setFinishingOptions,
  paperStocks,
  printers,
  selectedFinishes,
  setSelectedFinishes,
  finishingProcesses,
  hasNumbering,
  setHasNumbering,
  numberingStartAt,
  setNumberingStartAt,
  hasLooseShield,
  setHasLooseShield,
  isSettingFromQuoteInternal, // NEW PROP
}) => {
  const PERFORATE_PROCESS_ID = 'perforate';
  const perforateSelected = selectedFinishes.includes(PERFORATE_PROCESS_ID);
  const perforatedSheetIndexes = Array.isArray(
    finishingOptions?.[PERFORATE_PROCESS_ID]?.perforated_sheet_indexes
  )
    ? finishingOptions[PERFORATE_PROCESS_ID].perforated_sheet_indexes
    : [];
  const allSheetIndexes = Array.from({ length: sheetsPerSet }, (_, index) => index);

  // Log all props received by NCROptionsForm on every render
  console.log('NCROptionsForm: Component rendering with props:');
  console.log('  ncrType:', ncrType);
  console.log('  sheetsPerSet:', sheetsPerSet);
  console.log('  setsPerPad:', setsPerPad);
  console.log('  paperSelections (received):', paperSelections);
  console.log('  sheetPrinters (received):', sheetPrinters);
  console.log('  sheetSides (received):', sheetSides);
  console.log('  hasWraparoundShield:', hasWraparoundShield);
  console.log('  hasNumbering:', hasNumbering);
  console.log('  numberingStartAt:', numberingStartAt);
  console.log('  hasLooseShield:', hasLooseShield);
  console.log('  isSettingFromQuoteInternal:', isSettingFromQuoteInternal);
// Filter NCR paper stocks by layer
const topPapers = paperStocks.filter(stock => 
  stock.ncr_layer === 'top'
);

  
  const middlePapers = paperStocks.filter(stock => 
    stock.ncr_layer === 'middle'
  );
  
  const bottomPapers = paperStocks.filter(stock => 
    stock.ncr_layer === 'bottom'
  );

  // Initialize or update paper selections when sheets per set changes
  useEffect(() => {
    console.log('NCROptionsForm: paperSelections useEffect running. isSettingFromQuoteInternal:', isSettingFromQuoteInternal, 'sheetsPerSet:', sheetsPerSet, 'paperSelections.length:', paperSelections.length);
    // Guard against overwriting when loading from a quote/draft
    if (isSettingFromQuoteInternal) {
      return;
    }

    // Only adjust if the length is incorrect or if it's an array of empty strings
    // indicating the parent's auto-select might not have fully populated it.
    if (paperSelections.length !== sheetsPerSet || paperSelections.every(s => s === '')) {
      setPaperSelections(prevSelections => {
        const newArray = Array(sheetsPerSet).fill('');
        // When auto-initializing, we don't want to preserve previous selections
        // unless there's a specific auto-suggest logic. For now, just fill with empty.
        if (prevSelections && prevSelections.length > 0 && prevSelections.length === sheetsPerSet) {
          // This block is for preserving if sheetsPerSet didn't change but content became empty
        }
        return newArray;
      });
    }
    console.log('NCROptionsForm: paperSelections useEffect finished.');
  }, [sheetsPerSet, paperSelections.length, setPaperSelections]); // Add paperSelections.length to dependencies

  // Auto-select NCR gluing when needed
  useEffect(() => {
    console.log('NCROptionsForm: ncrType/finishingProcesses useEffect running. isSettingFromQuoteInternal:', isSettingFromQuoteInternal, 'ncrType:', ncrType);
    // Guard against overwriting when loading from a quote/draft
    if (isSettingFromQuoteInternal) {
      return;
    }
    const glueEdgeProcess = finishingProcesses.find(p =>
      p.id === 'ncr-padding' || p.id === 'pad' || p.id === 'padding'
    );

    if (glueEdgeProcess) {
      const glueProcessId = glueEdgeProcess.id;
      if (ncrType === 'pads') {
        // Auto-select padding finish when switching to pads
        if (!selectedFinishes.includes(glueProcessId)) {
          setSelectedFinishes(prev => [...prev, glueProcessId]);
        }
      } else if (ncrType === 'sets') {
        // Auto-deselect padding finish when switching to sets
        if (selectedFinishes.includes(glueProcessId)) {
          setSelectedFinishes(prev => prev.filter(id => id !== glueProcessId));
        }
      }
    }
    console.log('NCROptionsForm: ncrType/finishingProcesses useEffect finished. selectedFinishes:', selectedFinishes);
  }, [ncrType, finishingProcesses, selectedFinishes, setSelectedFinishes, isSettingFromQuoteInternal]);

  // Auto-select/deselect numbering finish
  useEffect(() => {
    console.log('NCROptionsForm: hasNumbering useEffect running. isSettingFromQuoteInternal:', isSettingFromQuoteInternal, 'hasNumbering:', hasNumbering);
    // Guard against overwriting when loading from a quote/draft
    if (isSettingFromQuoteInternal) {
      return;
    }
    const numberingProcessId = "number"; // Ensure this ID matches your data
    if (hasNumbering) {
      if (!selectedFinishes.includes(numberingProcessId)) {
        setSelectedFinishes(prev => [...prev, numberingProcessId]);
      }
    } else {
      if (selectedFinishes.includes(numberingProcessId)) {
        setSelectedFinishes(prev => prev.filter(id => id !== numberingProcessId));
      }
      console.log('NCROptionsForm: hasNumbering useEffect finished. selectedFinishes:', selectedFinishes);
      } 
  }, [hasNumbering, selectedFinishes, setSelectedFinishes, isSettingFromQuoteInternal]);

  // Auto-select/deselect wraparound shield finish
  useEffect(() => {
    console.log('NCROptionsForm: hasWraparoundShield useEffect running. isSettingFromQuoteInternal:', isSettingFromQuoteInternal, 'hasWraparoundShield:', hasWraparoundShield);
    // Guard against overwriting when loading from a quote/draft
    if (isSettingFromQuoteInternal) {
      return;
    }
    const wraparoundShieldProcessId = "wraparound-shield"; // Ensure this ID matches
    if (hasWraparoundShield) {
      if (!selectedFinishes.includes(wraparoundShieldProcessId)) {
        setSelectedFinishes(prev => [...prev, wraparoundShieldProcessId]);
      }
    } else {
      if (selectedFinishes.includes(wraparoundShieldProcessId)) {
        setSelectedFinishes(prev => prev.filter(id => id !== wraparoundShieldProcessId));
      }
      console.log('NCROptionsForm: hasWraparoundShield useEffect finished. selectedFinishes:', selectedFinishes);
      } 
  }, [hasWraparoundShield, selectedFinishes, setSelectedFinishes, isSettingFromQuoteInternal]);

  // Auto-select/deselect loose shield finish
  useEffect(() => {
    console.log('NCROptionsForm: hasLooseShield useEffect running. isSettingFromQuoteInternal:', isSettingFromQuoteInternal, 'hasLooseShield:', hasLooseShield);
    // Guard against overwriting when loading from a quote/draft
    if (isSettingFromQuoteInternal) {
      return;
    }
    const looseShieldProcessId = "loose-shield"; // Ensure this ID matches
    if (hasLooseShield) {
      if (!selectedFinishes.includes(looseShieldProcessId)) {
        setSelectedFinishes(prev => [...prev, looseShieldProcessId]);
      }
    } else {
      if (selectedFinishes.includes(looseShieldProcessId)) {
        setSelectedFinishes(prev => prev.filter(id => id !== looseShieldProcessId));
      }
      console.log('NCROptionsForm: hasLooseShield useEffect finished. selectedFinishes:', selectedFinishes);
    }
  }, [hasLooseShield, selectedFinishes, setSelectedFinishes]);

  useEffect(() => {
    if (!perforateSelected) {
      if (finishingOptions?.[PERFORATE_PROCESS_ID]?.perforated_sheet_indexes) {
        setFinishingOptions(prev => ({
          ...prev,
          [PERFORATE_PROCESS_ID]: {
            ...prev[PERFORATE_PROCESS_ID],
            perforated_sheet_indexes: [],
          }
        }));
      }
      return;
    }

    const validIndexes = perforatedSheetIndexes.filter(
      index => Number.isInteger(index) && index >= 0 && index < sheetsPerSet
    );
    const needsDefaultSelection = validIndexes.length === 0;
    const needsCleanup = validIndexes.length !== perforatedSheetIndexes.length;

    if (!needsDefaultSelection && !needsCleanup) {
      return;
    }

    setFinishingOptions(prev => ({
      ...prev,
      [PERFORATE_PROCESS_ID]: {
        ...prev[PERFORATE_PROCESS_ID],
        perforated_sheet_indexes: needsDefaultSelection ? allSheetIndexes : validIndexes,
      }
    }));
  }, [
    allSheetIndexes,
    finishingOptions,
    perforateSelected,
    perforatedSheetIndexes,
    setFinishingOptions,
    sheetsPerSet,
  ]);

  // Handle paper selection change for a specific position
  const handlePaperChange = (index, paperId) => {
    console.log(`NCROptionsForm: handlePaperChange - Setting paperSelections[${index}] to ${paperId}. Current prev:`, paperSelections);
    setPaperSelections(prev => {
      const updated = [...prev];
      updated[index] = paperId;
      return updated;
    });
  };

  // Handle printer selection change for a specific position
  const handlePrinterChange = (index, printerId) => {
    console.log(`NCROptionsForm: handlePrinterChange - Setting sheetPrinters[${index}] to ${printerId}. Current prev:`, sheetPrinters);
    setSheetPrinters(prev => {
      const updated = [...prev];
      updated[index] = printerId;
      return updated;
    });
  };

  // Handle sides selection change for a specific position
  const handleSidesChange = (index, sides) => {
    console.log(`NCROptionsForm: handleSidesChange - Setting sheetSides[${index}] to ${sides}. Current prev:`, sheetSides);
    setSheetSides(prev => {
      const updated = [...prev];
      updated[index] = sides;
      return updated;
    });
  };

  // Get appropriate paper options based on position in set
  const getPapersForPosition = (position) => {
    if (position === 0) {
      // Top sheet (first)
      return topPapers;
    } else if (position === sheetsPerSet - 1) {
      // Bottom sheet (last)
      return bottomPapers;
    } else {
      // Middle sheets
      return middlePapers;
    }
  };

  // Get layer name for position
  const getLayerName = (position) => {
    if (position === 0) return "Top sheet";
    if (position === sheetsPerSet - 1) return "Bottom sheet";
    return `Middle sheet ${position}`;
  };

  // Auto-suggest a typical color sequence
  const autosuggestColors = () => {
    const newSelections = [...paperSelections];
    
    // Typical colors: White (top), Yellow, Pink, Blue, Green (bottom)
    const colorSequence = [
      'white', 'yellow', 'pink', 'blue', 'green'
    ];
    
    // For each position, try to find a paper with matching color in the name
    for (let i = 0; i < sheetsPerSet; i++) {
      console.log(`NCROptionsForm: autosuggestColors - Processing sheet ${i}. Current selection: ${newSelections[i]}`);
      if (newSelections[i]) continue; // Skip if already selected
      
      const validPapers = getPapersForPosition(i);
      const targetColor = colorSequence[i % colorSequence.length];
      
      // Try to find a paper with the target color in its name
      const matchingPaper = validPapers.find(paper => 
        paper.name.toLowerCase().includes(targetColor.toLowerCase())
      );
      
      if (matchingPaper) {
        console.log(`NCROptionsForm: autosuggestColors - Found matching paper for sheet ${i}: ${matchingPaper.name} (ID: ${matchingPaper.id})`);
        newSelections[i] = matchingPaper.id;
      }
    }
    
    console.log('NCROptionsForm: autosuggestColors - Final newSelections:', newSelections);
    setPaperSelections(newSelections);
  };

  const handlePerforateToggle = (checked) => {
    if (checked) {
      if (!perforateSelected) {
        setSelectedFinishes(prev => [...prev, PERFORATE_PROCESS_ID]);
      }

      setFinishingOptions(prev => ({
        ...prev,
        [PERFORATE_PROCESS_ID]: {
          ...prev[PERFORATE_PROCESS_ID],
          perforated_sheet_indexes:
            Array.isArray(prev?.[PERFORATE_PROCESS_ID]?.perforated_sheet_indexes)
            && prev[PERFORATE_PROCESS_ID].perforated_sheet_indexes.length > 0
              ? prev[PERFORATE_PROCESS_ID].perforated_sheet_indexes
                  .filter(index => Number.isInteger(index) && index >= 0 && index < sheetsPerSet)
              : allSheetIndexes,
        }
      }));
      return;
    }

    if (perforateSelected) {
      setSelectedFinishes(prev => prev.filter(id => id !== PERFORATE_PROCESS_ID));
    }

    setFinishingOptions(prev => ({
      ...prev,
      [PERFORATE_PROCESS_ID]: {
        ...prev[PERFORATE_PROCESS_ID],
        perforated_sheet_indexes: [],
      }
    }));
  };

  const handlePerforatedSheetToggle = (index, checked) => {
    setFinishingOptions(prev => {
      const existingIndexes = Array.isArray(prev?.[PERFORATE_PROCESS_ID]?.perforated_sheet_indexes)
        ? prev[PERFORATE_PROCESS_ID].perforated_sheet_indexes
        : [];
      const nextIndexes = checked
        ? [...new Set([...existingIndexes, index])].sort((a, b) => a - b)
        : existingIndexes.filter(existingIndex => existingIndex !== index);

      return {
        ...prev,
        [PERFORATE_PROCESS_ID]: {
          ...prev[PERFORATE_PROCESS_ID],
          perforated_sheet_indexes: nextIndexes,
        }
      };
    });
  };

  return (
    <div className="md:col-span-2 border p-4 rounded bg-gray-50">
      <h3 className="font-medium mb-3">NCR Options</h3>

      {/* NCR Type Selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">NCR Type</label>
        <div className="flex">
          <button
            type="button"
            className={`flex-1 py-2 px-4 rounded-l-lg ${
              ncrType === 'sets'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800'
            }`}
            onClick={() => setNcrType('sets')}
          >
            Sets
          </button>
          <button
            type="button"
            className={`flex-1 py-2 px-4 rounded-r-lg ${
              ncrType === 'pads'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800'
            }`}
            onClick={() => setNcrType('pads')}
          >
            Pads
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Sets are individual multi-part forms. Pads are sets glued together along one edge.
        </p>
      </div>

      {/* Sheets Per Set */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Sheets Per Set</label>
        <select
          value={sheetsPerSet}
          onChange={(e) => setSheetsPerSet(Number(e.target.value))}
          className="w-full p-2 border rounded"
        >
          <option value="2">2-Part</option>
          <option value="3">3-Part</option>
          <option value="4">4-Part</option>
          <option value="5">5-Part</option>
        </select>
      </div>

      {/* Paper Selection for Each Sheet with Auto-Suggest */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium">Paper Selection</label>
          <button
            type="button"
            onClick={autosuggestColors}
            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            Auto-suggest typical colors
          </button>
        </div>
        
        <div className="space-y-3 border p-3 rounded bg-white">
          {Array.from({ length: sheetsPerSet }).map((_, index) => {
            const validPapers = getPapersForPosition(index);
            return (
              <div key={`sheet-${index}`} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium w-28">{getLayerName(index)}:</span>
                    <div className="flex-1 flex gap-2 items-start">
                      <select
                        value={paperSelections[index] || ''}
                        onChange={(e) => handlePaperChange(index, e.target.value)}
                      className="flex-1 p-2 border rounded"
                    >
                      <option value="">Select paper</option>
                      {validPapers.map(stock => (
                        <option key={stock.id} value={stock.id}>
                          {stock.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={sheetSides[index] || 2}
                      onChange={(e) => handleSidesChange(index, Number(e.target.value))}
                      className="flex-1 p-2 border rounded"
                    >
                      <option value={1}>Single-sided</option>
                      <option value={2}>Double-sided</option>
                    </select>
                      <select
                        value={sheetPrinters[index] || 'mono'}
                        onChange={(e) => handlePrinterChange(index, e.target.value)}
                        className="flex-1 p-2 border rounded"
                    >
                        <option value="mono">Black and White</option>
                        <option value="colour">Colour</option>
                      </select>
                      {perforateSelected && (
                        <label className="flex items-center gap-2 whitespace-nowrap rounded border bg-gray-50 px-3 py-2 text-sm">
                          <input
                            type="checkbox"
                            checked={perforatedSheetIndexes.includes(index)}
                            onChange={(e) => handlePerforatedSheetToggle(index, e.target.checked)}
                          />
                          Perforate
                        </label>
                      )}
                    </div>
                  </div>
                {paperSelections[index] && (
                  <p className="text-xs text-gray-500 ml-28">
                    {paperStocks.find(s => s.id === paperSelections[index])?.weight_gsm}gsm
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Each position requires a specific type of NCR paper to ensure proper transfer.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          If any layer is colour, all layers are charged at the colour click rate.
        </p>
      </div>

      {/* Pads-specific options */}
      {ncrType === 'pads' && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Sets Per Pad</label>
            <input
              type="number"
              value={setsPerPad}
              onChange={(e) => setSetsPerPad(Math.max(1, Number(e.target.value)))}
              className="w-full p-2 border rounded"
              min="1"
              max="100"
            />
            <p className="text-xs text-gray-500 mt-1">
              How many sets will be glued together in each pad?
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Pad Glue Edge</label>
            <select
              value={padGlueEdge}
              onChange={(e) => setPadGlueEdge(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="top">Top</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>

          {/* Card Back is now default for pads, so checkbox is removed */}
          {/* <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="cardBack"
                // checked={hasCardBack} // Removed
                // onChange={(e) => setHasCardBack(e.target.checked)} // Removed
                className="mr-2"
              />
              <label htmlFor="cardBack" className="font-medium">
                Add Card Back
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-5 mt-1">
              Adds a sturdy card backing to each pad (Now default for pads)
            </p>
          </div> */}

          {/* Card Stock Selector (Conditional) - Removed as card back is default */}
          {/* {hasCardBack && (
            <div className="mb-4 ml-5">
              <label className="block text-sm font-medium mb-1">Card Back Stock</label>
              <PaperSelector
                paperStocks={paperStocks.filter(stock => 
                  stock.weight_gsm >= 200 || 
                  stock.category?.toLowerCase().includes('card')
                )}
                // selectedPaperId={cardBackStock} // Removed
                // onChange={setCardBackStock} // Removed
                className="w-full"
              />
            </div>
          )} */}

          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="wraparoundShield"
                checked={hasWraparoundShield}
                onChange={(e) => setHasWraparoundShield(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="wraparoundShield" className="font-medium">
                Add Wraparound Writing Shield
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-5 mt-1">
              Adds a shield attached to the pad that wraps around to prevent transfer.
            </p>
          </div>

          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="looseShield"
                checked={hasLooseShield}
                onChange={(e) => setHasLooseShield(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="looseShield" className="font-medium">
                Add Loose Writing Shield
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-5 mt-1">
              Adds a separate, loose writing shield per pad.
            </p>
          </div>

        </>
      )}

      {/* Numbering Option */}
      <div className="mb-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasNumbering"
            checked={hasNumbering}
            onChange={(e) => setHasNumbering(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="hasNumbering" className="font-medium">
            Add Sequential Numbering
          </label>
        </div>
        {hasNumbering && (
          <div className="mt-2 ml-5">
            <label className="block text-sm font-medium mb-1">Start Numbering At:</label>
            <input
              type="number"
              value={numberingStartAt}
              onChange={(e) => setNumberingStartAt(Math.max(1, Number(e.target.value)))}
              className="w-full p-2 border rounded"
              min="1"
            />
          </div>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="hasPerforation"
            checked={perforateSelected}
            onChange={(e) => handlePerforateToggle(e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="hasPerforation" className="font-medium">
            Add Perforation
          </label>
        </div>
        <p className="text-xs text-gray-500 ml-5 mt-1">
          Charged per imposed sheet. All sheets are selected by default, and you can fine-tune perforation per sheet above.
        </p>
      </div>
    </div>
  );
};

export default NCROptionsForm;
